import { computed, inject, Injectable, Signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthData } from '@ess-front/shared/models';
import { AuthService } from '@ess-front/shared/auth';
import { ServerActionsStore } from '@ess-front/shared/websockets';
import { forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { UserDomainService } from '@app/auth/domain/user-domain.service';
import { MembersAreaStoriesStore } from '@app/users-area/domain/members-area-stories-store';
import { UserTripsDomainService } from '@app/users-area/domain/user-trips-domain.service';
import { UserTripsSignalStore } from '@app/users-area/domain/user-trips-signal-store';
import { User } from '@app/utils/models/user/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthDomainService {
  isAuthenticated$$: Signal<boolean>;

  // DI
  private readonly authService = inject(AuthService);
  private readonly matDialog = inject(MatDialog);
  private readonly membersAreaStoriesStore = inject(MembersAreaStoriesStore);
  private readonly serverActionsStore = inject(ServerActionsStore);
  private readonly userDomainService = inject(UserDomainService);
  private readonly userTripsDomainService = inject(UserTripsDomainService);
  private readonly userTripsSignalStore = inject(UserTripsSignalStore);

  constructor() {
    this.isAuthenticated$$ = computed(() => !!this.userDomainService.user$$());
  }

  isAuthenticatedInStorage(): boolean {
    return this.authService.isAuthenticated();
  }

  loginFromStorage$(): Observable<User | null> {
    if (this.authService.isAuthenticated()) {
      return this.userDomainService.initUserFromStorage$();
    } else {
      return of(null);
    }
  }

  login$(email: string, password: string, keepLogged: boolean): Observable<AuthData> {
    return this.authService.login(email, password, keepLogged).pipe(
      switchMap(authData => forkJoin([this.userDomainService.initUserFromStorage$(), of(authData)])),
      map(([, authData]) => authData),
    );
  }

  getStorageAuthData(): AuthData | null {
    return this.authService.getAuthData();
  }

  saveAuthData(authData: AuthData, keepData: boolean): void {
    this.authService.saveAuthData(authData, keepData);
  }

  clearStorageAuthData(): void {
    this.authService.clearAuthData();
  }

  logout(redirectUrl?: string): void {
    this.serverActionsStore.disconnect();
    this.userDomainService.clearCurrentUser();
    this.userTripsDomainService.unloadTrips();
    // The members area keeps its trips between visits, so the next member logging in on this tab must not see them.
    this.userTripsSignalStore.unloadTrips();
    // Same for the stories, which carry the member's bookmarks and trips near their city.
    this.membersAreaStoriesStore.unloadStories();
    this.authService.logout(redirectUrl || null);
  }

  handleActionWithAuthentication$(): Observable<boolean> {
    if (this.isAuthenticated$$()) {
      return of(true);
    } else {
      return from(this.openLoginModal()).pipe(
        switchMap(dialogRef => dialogRef.afterClosed()),
        map(result => !!result),
      );
    }
  }

  private async openLoginModal(): Promise<MatDialogRef<unknown>> {
    const { LoginModalComponent } = await import('../ui/login-modal/login-modal.component'); // To break circular dependency LoginModalComponent -> LoginComponent --> AuthDomainService
    return this.matDialog.open(LoginModalComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100vh',
      width: '100vw',
      autoFocus: false,
    });
  }
}
